import { useState, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import type { GroupWithMembers } from '../../../../services/api/groups';
import { groupsApi } from '../../../../services/api/groups';
import type { CohortCoachWithDetails } from '../../../../services/api/cohortCoaches';

/** Which selection flow the user is currently in */
export type SelectionMode = 'none' | 'coach-first' | 'group-first';

interface UseCoachAssignmentOptions {
  cohortId: string;
  groups: GroupWithMembers[];
  onRefresh: () => void | Promise<void>;
  fetchCalloutData: () => Promise<void>;
}

export interface UseCoachAssignmentResult {
  /** The coach currently selected for multi-group assignment, or null */
  selectedCoachForAssignment: CohortCoachWithDetails | null;
  /** Set of group IDs chosen for the selected coach */
  selectedGroupIdsForAssignment: Set<string>;
  /** Whether we are currently in coach selection mode */
  isCoachSelectionMode: boolean;
  /** Which selection flow the user is in */
  selectionMode: SelectionMode;
  /** Set of group IDs selected in group-first mode (before a coach is picked) */
  groupFirstSelectedIds: Set<string>;
  /** Trigger value to force CoachesPanel remount on assignment changes */
  coachPanelRefreshTrigger: number;
  /** Group being assigned a coach via the single-group modal */
  groupForCoachAssign: GroupWithMembers | null;
  /** Coach being sent a reminder */
  coachForReminder: CohortCoachWithDetails | null;

  // ── Actions ──
  /** Toggle / select a coach for multi-group assignment (coach-first flow) */
  handleCoachSelect: (coach: CohortCoachWithDetails) => void;
  /** Cancel the multi-group coach selection mode */
  handleCancelCoachSelection: () => void;
  /** Toggle a group's selection for the current coach (coach-first flow) */
  handleToggleGroupSelection: (groupId: string) => void;
  /** Toggle a group's selection in group-first mode (no coach selected yet) */
  handleGroupFirstToggle: (groupId: string) => void;
  /** Select a coach while in group-first mode — transitions to callout bar */
  handleCoachSelectInGroupFirst: (coach: CohortCoachWithDetails) => void;
  /** Check if a coach is available for a specific group's time slot */
  isCoachAvailableForGroup: (coach: CohortCoachWithDetails, group: GroupWithMembers) => boolean;
  /** Check if a coach is available for ALL group-first selected groups */
  isCoachAvailableForAllSelectedGroups: (coach: CohortCoachWithDetails) => boolean;
  /** Groups that already have a coach among the selected groups */
  getGroupsWithExistingCoach: () => GroupWithMembers[];
  /** Execute the coach-to-groups assignment */
  handleAssignCoachToGroups: () => Promise<void>;
  /** Open single-group coach assignment modal */
  handleChangeCoach: (group: GroupWithMembers) => void;
  /** Clear single-group coach assignment state */
  clearGroupForCoachAssign: () => void;
  /** Open the send-reminder flow for a coach */
  handleSendCoachReminder: (coach: CohortCoachWithDetails) => void;
  /** Clear the coach reminder state */
  clearCoachForReminder: () => void;
}

/**
 * Manages the coach-to-group assignment workflow including the multi-group
 * selection mode, availability validation, and single-group coach modal state.
 */
export function useCoachAssignment({
  cohortId,
  groups,
  onRefresh,
  fetchCalloutData,
}: UseCoachAssignmentOptions): UseCoachAssignmentResult {
  // ── Multi-group selection mode ──
  const [selectedCoachForAssignment, setSelectedCoachForAssignment] =
    useState<CohortCoachWithDetails | null>(null);
  const [selectedGroupIdsForAssignment, setSelectedGroupIdsForAssignment] =
    useState<Set<string>>(new Set());

  // ── Group-first selection mode ──
  const [groupFirstSelectedIds, setGroupFirstSelectedIds] =
    useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('none');

  const isCoachSelectionMode = selectedCoachForAssignment !== null;

  // ── CoachesPanel refresh trigger ──
  const [coachPanelRefreshTrigger, setCoachPanelRefreshTrigger] = useState(0);

  // ── Single-group coach assignment ──
  const [groupForCoachAssign, setGroupForCoachAssign] = useState<GroupWithMembers | null>(null);
  const [coachForReminder, setCoachForReminder] = useState<CohortCoachWithDetails | null>(null);

  // ── Shared availability check ──
  const isCoachAvailableForGroup = useCallback(
    (coach: CohortCoachWithDetails, group: GroupWithMembers): boolean => {
      if (coach.availability_status === 'pending_response') return false;
      if (coach.availability_status === 'unavailable') return false;
      if (!group.scheduled_time_slot_id) return false;
      if (!coach.confirmed_time_slots || coach.confirmed_time_slots.length === 0) return false;
      return coach.confirmed_time_slots.includes(group.scheduled_time_slot_id);
    },
    [],
  );

  /** Check if a coach is available for ALL group-first selected groups */
  const isCoachAvailableForAllSelectedGroups = useCallback(
    (coach: CohortCoachWithDetails): boolean => {
      if (groupFirstSelectedIds.size === 0) return true;
      const selectedGroups = groups.filter((g) => groupFirstSelectedIds.has(g.id));
      return selectedGroups.every((group) => isCoachAvailableForGroup(coach, group));
    },
    [groups, groupFirstSelectedIds, isCoachAvailableForGroup],
  );

  // ── Coach-first handlers ──
  const handleCoachSelect = useCallback(
    (coach: CohortCoachWithDetails) => {
      // If we're in group-first mode, delegate to handleCoachSelectInGroupFirst
      if (selectionMode === 'group-first') {
        // Toggle: clicking the already-selected coach deselects
        if (selectedCoachForAssignment?.id === coach.id) {
          setSelectedCoachForAssignment(null);
          // Stay in group-first mode with groups still selected
        } else {
          setSelectedCoachForAssignment(coach);
          // Transfer group-first selections into the assignment set
          setSelectedGroupIdsForAssignment(new Set(groupFirstSelectedIds));
        }
        return;
      }

      // Coach-first flow: toggle coach selection
      if (selectedCoachForAssignment?.id === coach.id) {
        setSelectedCoachForAssignment(null);
        setSelectedGroupIdsForAssignment(new Set());
        setSelectionMode('none');
      } else {
        setSelectedCoachForAssignment(coach);
        setSelectedGroupIdsForAssignment(new Set());
        setSelectionMode('coach-first');
      }
    },
    [selectedCoachForAssignment, selectionMode, groupFirstSelectedIds],
  );

  const handleCancelCoachSelection = useCallback(() => {
    setSelectedCoachForAssignment(null);
    setSelectedGroupIdsForAssignment(new Set());
    setGroupFirstSelectedIds(new Set());
    setSelectionMode('none');
  }, []);

  const handleToggleGroupSelection = useCallback((groupId: string) => {
    setSelectedGroupIdsForAssignment((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  // ── Group-first handlers ──

  /** Toggle a group in group-first mode (no coach selected yet) */
  const handleGroupFirstToggle = useCallback((groupId: string) => {
    setGroupFirstSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
        // If all groups deselected, return to none
        if (next.size === 0) {
          setSelectionMode('none');
        }
      } else {
        next.add(groupId);
        setSelectionMode('group-first');
      }
      return next;
    });
  }, []);

  /** Select a coach while in group-first mode — transitions to callout bar */
  const handleCoachSelectInGroupFirst = useCallback(
    (coach: CohortCoachWithDetails) => {
      if (selectedCoachForAssignment?.id === coach.id) {
        // Deselect coach, stay in group-first with groups still selected
        setSelectedCoachForAssignment(null);
        setSelectedGroupIdsForAssignment(new Set());
        return;
      }
      setSelectedCoachForAssignment(coach);
      // Transfer group-first selections into the assignment set
      setSelectedGroupIdsForAssignment(new Set(groupFirstSelectedIds));
    },
    [selectedCoachForAssignment, groupFirstSelectedIds],
  );

  // ── Handle group deselection in group-first mode when coach is already picked ──
  const handleToggleGroupInGroupFirstWithCoach = useCallback((groupId: string) => {
    setSelectedGroupIdsForAssignment((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      // Also sync the group-first set
      setGroupFirstSelectedIds(new Set(next));
      // If all groups removed, cancel entire flow
      if (next.size === 0) {
        setSelectedCoachForAssignment(null);
        setSelectionMode('none');
      }
      return next;
    });
  }, []);

  const getGroupsWithExistingCoach = useCallback(
    () => groups.filter((group) => selectedGroupIdsForAssignment.has(group.id) && group.coach_id),
    [groups, selectedGroupIdsForAssignment],
  );

  const handleAssignCoachToGroups = useCallback(async () => {
    if (!selectedCoachForAssignment || selectedGroupIdsForAssignment.size === 0) return;

    try {
      for (const groupId of selectedGroupIdsForAssignment) {
        await groupsApi.update(cohortId, groupId, { coachId: selectedCoachForAssignment.user_id });
      }

      const coachName =
        `${selectedCoachForAssignment.first_name || ''} ${selectedCoachForAssignment.last_name || ''}`.trim();
      notifications.show({
        title: 'Coach Assigned',
        message: `${coachName} assigned to ${selectedGroupIdsForAssignment.size} group(s)`,
        color: 'green',
      });

      setSelectedCoachForAssignment(null);
      setSelectedGroupIdsForAssignment(new Set());
      setGroupFirstSelectedIds(new Set());
      setSelectionMode('none');
      setCoachPanelRefreshTrigger((prev) => prev + 1);
      await onRefresh();
      await fetchCalloutData();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to assign coach to groups',
        color: 'red',
      });
    }
  }, [
    cohortId,
    selectedCoachForAssignment,
    selectedGroupIdsForAssignment,
    onRefresh,
    fetchCalloutData,
  ]);

  const handleChangeCoach = useCallback((group: GroupWithMembers) => {
    setGroupForCoachAssign(group);
  }, []);

  const clearGroupForCoachAssign = useCallback(() => {
    setGroupForCoachAssign(null);
  }, []);

  const handleSendCoachReminder = useCallback((coach: CohortCoachWithDetails) => {
    setCoachForReminder(coach);
  }, []);

  const clearCoachForReminder = useCallback(() => {
    setCoachForReminder(null);
  }, []);

  // Use the appropriate group toggle handler based on current mode
  const effectiveToggleGroupSelection = useCallback(
    (groupId: string) => {
      if (selectionMode === 'group-first' && selectedCoachForAssignment) {
        handleToggleGroupInGroupFirstWithCoach(groupId);
      } else {
        handleToggleGroupSelection(groupId);
      }
    },
    [selectionMode, selectedCoachForAssignment, handleToggleGroupInGroupFirstWithCoach, handleToggleGroupSelection],
  );

  return {
    selectedCoachForAssignment,
    selectedGroupIdsForAssignment,
    isCoachSelectionMode,
    selectionMode,
    groupFirstSelectedIds,
    coachPanelRefreshTrigger,
    groupForCoachAssign,
    coachForReminder,
    handleCoachSelect,
    handleCancelCoachSelection,
    handleToggleGroupSelection: effectiveToggleGroupSelection,
    handleGroupFirstToggle,
    handleCoachSelectInGroupFirst,
    isCoachAvailableForGroup,
    isCoachAvailableForAllSelectedGroups,
    getGroupsWithExistingCoach,
    handleAssignCoachToGroups,
    handleChangeCoach,
    clearGroupForCoachAssign,
    handleSendCoachReminder,
    clearCoachForReminder,
  };
}
